/**
 * Contracts for the `add_tag` and `remove_tag` action nodes.
 *
 * `resource` is a template path to a public id in the run context, not a
 * literal id, so one node covers whatever the run is about; the resource type
 * is derived from that id's prefix at execution time.
 *
 * @see docs/exec-plans/active/tagging-email-threads-and-messages.md
 */

import { z } from "zod";

export const tagActionInputSchema = z.object({}).passthrough();

export const tagActionConfigSchema = z
  .object({
    tag: z
      .string()
      .trim()
      .min(1, "Choose the tag this step applies.")
      .describe("Tag ref — a `tag_…` id or a name matched case-insensitively."),
    resource: z
      .string()
      .trim()
      .min(1, "Name the resource this step tags, for example input.resourceId.")
      .describe("Path to a public id in the run context, e.g. `input.resourceId`."),
  })
  .passthrough();

export const tagActionOutputSchema = z.object({
  resourceId: z.string().describe("Public id of the resource that was tagged."),
  resourceType: z.string().describe("Taggable resource type, derived from the id prefix."),
  changed: z.boolean().describe("False when the resource already had (or lacked) the tag."),
  tags: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        color: z.string(),
        tag_resource_id: z.string(),
      }),
    )
    .describe("The resource's tags after the step ran."),
});

export type TagActionConfig = z.infer<typeof tagActionConfigSchema>;
export type TagActionOutput = z.infer<typeof tagActionOutputSchema>;
